import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Trash2, Save, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SplashSettingsForm = ({ onSuccess }) => {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    register_title: "Register Your Package",
    register_text: "To avoid delays in delivery, please register your package in our Customer Portal before it arrives at our warehouse.",
    bp_title: "Don't Forget Your BP Number!",
    bp_number: "BP-137803",
    bp_text: "When shipping, always include this number. Add this to your shipping address to ensure proper routing.",
    prohibited_title: "Prohibited Items",
    prohibited_text: "Please DO NOT ship the following:",
    prohibited_items: ["Liquids", "Perishable Food", "Lithium Battery", "Illegal Items"]
  });
  const [newItem, setNewItem] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/splash`);
      setSettings(res.data);
    } catch (err) {
      console.log("Using default splash settings");
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const addProhibitedItem = () => {
    if (newItem.trim() && !settings.prohibited_items.includes(newItem.trim())) {
      setSettings(prev => ({
        ...prev,
        prohibited_items: [...prev.prohibited_items, newItem.trim()]
      }));
      setNewItem("");
    }
  };

  const removeProhibitedItem = (index) => {
    setSettings(prev => ({
      ...prev,
      prohibited_items: prev.prohibited_items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/settings/splash`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Splash screen settings updated!");
      if (onSuccess) onSuccess(settings);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Section 1: Register Package */}
      <div className="bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 rounded-lg p-4 space-y-3">
        <h4 className="font-bold text-sm uppercase tracking-wider text-orange-700 dark:text-orange-400 flex items-center gap-2">
          <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">1</span>
          Register Package Section
        </h4>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Title</Label>
          <Input
            value={settings.register_title}
            onChange={(e) => handleChange("register_title", e.target.value)}
            placeholder="Section title"
            data-testid="splash-register-title"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Message Text</Label>
          <Textarea
            value={settings.register_text}
            onChange={(e) => handleChange("register_text", e.target.value)}
            placeholder="Message to display"
            rows={2}
            data-testid="splash-register-text"
          />
        </div>
      </div>

      {/* Section 2: BP Number */}
      <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4 space-y-3">
        <h4 className="font-bold text-sm uppercase tracking-wider text-blue-700 dark:text-blue-400 flex items-center gap-2">
          <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs">2</span>
          BP Number Section
        </h4>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Title</Label>
          <Input
            value={settings.bp_title}
            onChange={(e) => handleChange("bp_title", e.target.value)}
            placeholder="Section title"
            data-testid="splash-bp-title"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">BP Number</Label>
          <Input
            value={settings.bp_number}
            onChange={(e) => handleChange("bp_number", e.target.value)}
            placeholder="BP-XXXXXX"
            className="font-mono font-bold text-lg"
            data-testid="splash-bp-number"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Description</Label>
          <Textarea
            value={settings.bp_text}
            onChange={(e) => handleChange("bp_text", e.target.value)}
            placeholder="Additional instructions"
            rows={2}
            data-testid="splash-bp-text"
          />
        </div>
      </div>

      {/* Section 3: Prohibited Items */}
      <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg p-4 space-y-3">
        <h4 className="font-bold text-sm uppercase tracking-wider text-red-700 dark:text-red-400 flex items-center gap-2">
          <span className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">3</span>
          Prohibited Items Section
        </h4>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Title</Label>
          <Input
            value={settings.prohibited_title}
            onChange={(e) => handleChange("prohibited_title", e.target.value)}
            placeholder="Section title"
            data-testid="splash-prohibited-title"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Warning Text</Label>
          <Input
            value={settings.prohibited_text}
            onChange={(e) => handleChange("prohibited_text", e.target.value)}
            placeholder="Warning message"
            data-testid="splash-prohibited-text"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Prohibited Items List</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {settings.prohibited_items.map((item, index) => (
              <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 rounded-full text-sm">
                {item}
                <button
                  type="button"
                  onClick={() => removeProhibitedItem(index)}
                  className="ml-1 hover:text-red-900 dark:hover:text-red-100"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="Add new prohibited item"
              onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addProhibitedItem())}
              data-testid="splash-new-item"
            />
            <Button
              type="button"
              variant="outline"
              onClick={addProhibitedItem}
              className="shrink-0"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-widest"
        data-testid="splash-save-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {loading ? "Saving..." : "Save Splash Settings"}
      </Button>
    </form>
  );
};

export default SplashSettingsForm;
